import { useEffect } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { useFieldArray, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiControlledSelectBox,
  MiControlledTextField,
  MiReorderButton,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { Box, Grid, Theme, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IAccountDrawsData } from "../../CustomerPricingDetails/CustomerPricingDetails.types";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import { customerDrawSetupTabSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerDrawsSetupTabViewModel } from "./useCustomerDrawsSetupTabViewModel";

export function CustomerDrawsSetupTab() {
  const { classes, theme } = useStyles();
  const { accountDetails, setSaveHandler, setConfirmModalConfig } =
    useCustomerPricingDetailsContext();

  const form = useForm<IAccountDrawsData>({
    mode: "onChange",
    defaultValues: { draws: [] },
    resolver: zodResolver(customerDrawSetupTabSchema),
  });
  const {
    control,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = form;

  const fieldArray = useFieldArray({
    control,
    name: "draws",
    rules: { minLength: 1 },
  });
  const { fields, move } = fieldArray;

  const {
    draws,
    orderTemplates,
    getDraws,
    getOrderTemplates,
    handleAddDraw,
    handleRemoveDraw,
    handleSaveDraws,
    handleCalculateTotal,
    isLoading,
  } = useCustomerDrawsSetupTabViewModel({ form, fieldArray });

  const currentDraws = watch("draws");

  const orderSelections = orderTemplates.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  useTabFormRegistration(form, isLoading);

  const hasDrawBeenDeleted = () =>
    draws.some(
      (draw) => !currentDraws.some((currentDraw) => currentDraw.id === draw.id)
    );

  useEffect(() => {
    if (accountDetails) {
      // form data is set in view model
      getDraws(accountDetails.id);
      getOrderTemplates({ sort: "name" });
    }
  }, [accountDetails]);

  useEffect(() => {
    // register save handler for this tab
    setSaveHandler(async () => {
      const values = getValues();
      const id = accountDetails?.id ?? "";

      return handleSaveDraws(values, id, true);
    });
  }, [accountDetails, draws]);

  useEffect(() => {
    if (draws.length === 0) {
      setConfirmModalConfig(null);
      return;
    }
    if (hasDrawBeenDeleted()) {
      setConfirmModalConfig({
        title: "Delete Draw?",
        description:
          "Saving will remove the draw(s) and reset all category and drop distributions to the new defaults. Would you like to continue?",
        textPositive: "Delete & Reset Draws",
        buttonPositiveColor: BUTTON_COLORS.RED,
      });
    } else {
      setConfirmModalConfig({
        title: "Apply New Defaults?",
        description:
          "Would you like to reset all category and drop distributions to the new defaults, or keep your current values?",
        textPositive: "Apply New Defaults",
        textSecondaryPositive: "Keep Current Values",
        hideNegative: true,
        handleSecondaryPositive: async () => {
          const values = getValues();
          return handleSaveDraws(values, accountDetails?.id ?? "", false);
        },
      });
    }
  }, [accountDetails, draws, currentDraws]);

  return (
    <ConfigAccordionHeader
      title="Default Draw Distribution"
      disableCollapse
      customAction={
        <MiButton
          title="Add Draw"
          onClick={() => {
            handleAddDraw();
            trigger("drawTotal");
          }}
          variant={BUTTON_VARIANTS.CONTAINED}
          startIcon={{ path: mdiPlus }}
          size="small"
          disabled={isLoading}
        />
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BODY2}
          color={theme.palette.text.secondary}
        >
          Set the percentage of payment drawn at each phase. Total must equal
          100%. This distribution applies to all Categories by default.
        </Typography>
        <Box className={classes.scrollable}>
          <Flipper flipKey={fields.map((draw) => draw.id).join("-")}>
            <Grid
              container
              alignItems="center"
              spacing={2}
              rowSpacing={3}
              sx={{ width: "100%" }}
            >
              {fields.map((field, index) => (
                <Flipped key={field.id} flipId={field.id}>
                  <Grid
                    container
                    size={12}
                    key={field.id}
                    spacing={2}
                    sx={{ alignItems: "top" }}
                  >
                    <Grid size={3}>
                      <MiControlledTextField
                        label="Draw Name"
                        name={`draws.${index}.name`}
                        disabled={isLoading}
                        formControl={control}
                        placeholder={`Enter Draw #${index + 1} name`}
                        inputProps={{ maxLength: 32 }}
                      />
                    </Grid>
                    <Grid size={3}>
                      <MiControlledSelectBox
                        label="Order"
                        name={`draws.${index}.orderTemplateId`}
                        disabled={isLoading}
                        options={orderSelections}
                        formControl={control}
                        required
                        disableNone
                      />
                    </Grid>
                    <Grid size={3}>
                      <NumericFormat
                        customInput={MiControlledTextField}
                        thousandSeparator
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        allowLeadingZeros={false}
                        suffix="%"
                        name={`draws.${index}.percentage`}
                        disabled={isLoading}
                        formControl={control}
                        label="%"
                        value={
                          Number.isNaN(getValues(`draws.${index}.percentage`))
                            ? undefined
                            : getValues(`draws.${index}.percentage`)
                        }
                        onValueChange={({ floatValue }) => {
                          setValue(
                            `draws.${index}.percentage`,
                            floatValue ?? Number.NaN,
                            {
                              shouldValidate: true,
                              shouldDirty: true,
                            }
                          );
                          handleCalculateTotal(getValues("draws"));
                          if (getValues("draws").length > 1) {
                            trigger("drawTotal");
                          }
                        }}
                        onBlur={() => {
                          // avoid redundant error checking onChange when only one draw exists
                          if (getValues("draws").length === 1) {
                            trigger("drawTotal");
                          }
                        }}
                      />
                    </Grid>
                    <Grid size={1} className={classes.reorderBtn}>
                      <MiReorderButton
                        onMoveUp={() => {
                          move(index, index - 1);
                          setValue(`draws.${index}.displayOrder`, index - 1);
                        }}
                        onMoveDown={() => {
                          move(index, index + 1);
                          setValue(`draws.${index}.displayOrder`, index + 1);
                        }}
                        disabled={isLoading}
                        disableUp={index === 0}
                        disableDown={index === getValues("draws").length - 1}
                        disableRipple
                      />
                    </Grid>
                    <Grid size={1} sx={{ alignSelf: "center" }}>
                      <MiButton
                        startIcon={{
                          path: mdiMinus,
                        }}
                        color={BUTTON_COLORS.RED}
                        variant={BUTTON_VARIANTS.CONTAINED}
                        className={classes.delBtn}
                        disabled={
                          isLoading ||
                          (index === 0 && getValues("draws").length <= 1)
                        }
                        onClick={() => {
                          handleRemoveDraw(index);
                          trigger("drawTotal");
                        }}
                      />
                    </Grid>
                  </Grid>
                </Flipped>
              ))}
              <Grid size={6} />
              {getValues("draws").length > 0 && (
                <Grid size={3} className={classes.total}>
                  <NumericFormat
                    customInput={MiControlledTextField}
                    formControl={control}
                    name="drawTotal"
                    label="Total Distribution (%)"
                    value={getValues("drawTotal")}
                    thousandSeparator
                    decimalScale={2}
                    allowNegative={false}
                    suffix="%"
                    readOnly
                    disabled
                    sx={{
                      ...(errors.drawTotal && {
                        "& .MuiFormHelperText-root.Mui-disabled": {
                          color: (muiTheme: Theme) =>
                            muiTheme.palette.error.main,
                        },
                      }),
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Flipper>
        </Box>
      </Box>
    </ConfigAccordionHeader>
  );
}

const useStyles = makeStyles()((theme) => ({
  delBtn: {
    borderRadius: "50%",
    padding: theme.spacing(0),
  },
  reorderBtn: {
    display: "flex",
    justifyContent: "center",
    alignSelf: "center",
    "& > *": {
      width: "50%",
    },
  },
  total: {
    paddingTop: `${theme.spacing(6)} !important`,
  },
  scrollable: {
    overflowY: "auto",
    flex: 1,
    paddingTop: theme.spacing(2),
  },
}));
